import { defineStore } from 'pinia'

interface CampaignsFilters {
    status: string[]
    period: string
    search: string
    starts_after: string
    starts_before: string
    routing_at: string
}

interface SortItem {
    key: string
    order: 'asc' | 'desc'
}

interface VolumeStatsItem {
    date: string
    volume: number
}

interface PerformanceStatsItem {
    date: string
    ctr: number
    deliverability_rate: number
}

interface CampaignsState {
    items: Campaign[]
    meta: ApiMetaResponseData | null
    filters: CampaignsFilters
    loading: boolean
    isRefreshing: boolean // Flag to prevent concurrent refresh calls
    // Table pagination/sorting
    page: number
    itemsPerPage: number
    sortBy: SortItem[]
    // Dashboard stats
    globalStats: {
        active_count: number
        scheduled_count: number
        error_count: number
    }
    recentCampaigns: Campaign[]
    // Stats from new API
    volumeStats: VolumeStatsItem[]
    performanceStats: PerformanceStatsItem[]
    statsLoading: boolean
}

const ERROR_STATUSES = [
    'QUERY_FAILED',
    'ROUTING_FAILED',
    'MESSAGE_GENERATION_FAILED',
    'SHORT_URL_SUFFIX_FAILED',
    'SHORT_URL_ERROR',
]

export const useCampaignsStore = defineStore('campaigns', {
    state: (): CampaignsState => ({
        items: [],
        meta: null,
        filters: {
            status: [],
            period: 'today',
            search: '',
            starts_after: '',
            starts_before: '',
            routing_at: '',
        },
        loading: false,
        isRefreshing: false,
        // Table pagination/sorting
        page: 1,
        itemsPerPage: 10,
        sortBy: [{ key: 'updated_at', order: 'desc' }],
        globalStats: {
            active_count: 0,
            scheduled_count: 0,
            error_count: 0,
        },
        recentCampaigns: [],
        volumeStats: [],
        performanceStats: [],
        statsLoading: false,
    }),

    getters: {
        getCampaignById: (state) => (id: string): Campaign | undefined =>
            state.items.find(c => c.id === id),

        errorCampaigns: (state): Campaign[] =>
            state.items.filter(c => ERROR_STATUSES.includes(c.status)),

        totalItems: (state): number => state.meta?.total || 0,

        // Computed table params for API call
        tableParams: (state): Record<string, unknown> => {
            const params: Record<string, unknown> = {
                page: state.page,
                perPage: state.itemsPerPage,
            }

            // Filters
            if (state.filters.status.length) {
                params['filter[status][]'] = state.filters.status
            }
            if (state.filters.search) {
                params['filter[name]'] = state.filters.search
            }
            if (state.filters.routing_at) {
                params['filter[routing_at]'] = state.filters.routing_at
            }
            if (state.filters.starts_after) {
                params['filter[starts_after]'] = state.filters.starts_after
            }
            if (state.filters.starts_before) {
                params['filter[starts_before]'] = state.filters.starts_before
            }

            // Sorting
            if (state.sortBy.length) {
                const sortStr = state.sortBy
                    .map(s => (s.order === 'desc' ? '-' : '') + s.key)
                    .join(',')
                params['sort'] = sortStr
            }

            return params
        },
    },

    actions: {
        // Table management actions
        setPage(page: number) {
            this.page = page
            this.fetchCampaigns()
        },

        setItemsPerPage(count: number) {
            this.itemsPerPage = count
            this.page = 1 // Reset to first page when changing page size
            this.fetchCampaigns()
        },

        setSorting(sortBy: SortItem[]) {
            this.sortBy = sortBy
            this.fetchCampaigns()
        },

        async fetchCampaigns(params: Record<string, unknown> = {}) {
            // Prevent concurrent refresh calls from WebSocket
            if (this.isRefreshing) {
                return
            }

            this.loading = true
            try {
                const { $customFetch } = useNuxtApp()

                const queryParams: Record<string, unknown> = {
                    include: 'router,sentCount,recipientsCount,deliveredCount,dispatchedCount',
                    hidePreview: 1,
                    ...this.tableParams,
                    ...params,
                }

                const response = await $customFetch<ApiResponseList<Campaign>>('/api/campaigns', {
                    method: 'GET',
                    params: queryParams,
                })

                this.items = response.data
                this.meta = response.meta

                if (this.filters.period === 'errors') {
                    this.globalStats.error_count = response.meta?.total || 0
                }
            } finally {
                this.loading = false
            }
        },

        async refreshOneCampaign(id: string, forceFresh: boolean = false) {
            try {
                const { $customFetch } = useNuxtApp()
                const headers: Record<string, string> = {}
                if (forceFresh) {
                    headers['X-Force-Fresh'] = '1'
                }

                const { data } = await $customFetch<ApiResponse<Campaign>>(`/api/campaigns/${id}`, {
                    method: 'GET',
                    params: { include: 'router,sentCount,recipientsCount,deliveredCount' },
                    headers,
                })

                const index = this.items.findIndex(c => c.id === id)
                if (index !== -1) {
                    this.items[index] = data
                }

                // Also update in recent campaigns
                const recentIndex = this.recentCampaigns.findIndex(c => c.id === id)
                if (recentIndex !== -1) {
                    this.recentCampaigns[recentIndex] = data
                }
            } catch (e) {
                console.error('Failed to refresh campaign:', e)
            }
        },

        async fetchRecentCampaigns() {
            try {
                const { $customFetch } = useNuxtApp()
                const response = await $customFetch<ApiResponseList<Campaign>>('/api/campaigns', {
                    params: {
                        perPage: 5,
                        sort: '-routing_at',
                        hidePreview: 1,
                    },
                })
                this.recentCampaigns = response.data
            } catch (e) {
                console.error('Failed to fetch recent campaigns:', e)
            }
        },

        async fetchErrorCount() {
            try {
                const { $customFetch } = useNuxtApp()
                const response = await $customFetch<ApiResponseList<Campaign>>('/api/campaigns', {
                    params:{
                        'filter[status][]': ERROR_STATUSES,
                        perPage: 1,
                        hidePreview: 1,
                    },
                })
                this.globalStats.error_count = response.meta?.total || 0
            } catch (e) {
                console.error('Failed to fetch error count:', e)
            }
        },

        async fetchGlobalStats(startDate: string, endDate: string) {
            try {
                const { $customFetch } = useNuxtApp()
                const dayjs = useDayjs()
                const today = dayjs()

                const [activeRes, scheduledRes, errorRes] = await Promise.all([
                    $customFetch<ApiResponseList<Campaign>>('/api/campaigns', {
                        params: {
                            'filter[routing_at]': today.format('YYYY-MM-DD'),
                            perPage: 1,
                            hidePreview: 1,
                        },
                    }),
                    $customFetch<ApiResponseList<Campaign>>('/api/campaigns', {
                        params: {
                            'filter[starts_after]': today.format('YYYY-MM-DD'),
                            perPage: 1,
                            hidePreview: 1,
                        },
                    }),
                    $customFetch<ApiResponseList<Campaign>>('/api/campaigns', {
                        params: {
                            'filter[status][]': ERROR_STATUSES,
                            perPage: 1,
                            hidePreview: 1,
                        },
                    }),
                ])

                this.globalStats = {
                    active_count: activeRes.meta?.total || 0,
                    scheduled_count: scheduledRes.meta?.total || 0,
                    error_count: errorRes.meta?.total || 0,
                }
            } catch (e) {
                console.error('Failed to fetch global stats:', e)
            }
        },

        updateFilters(newFilters: Partial<CampaignsFilters>) {
            this.filters = { ...this.filters, ...newFilters }
        },

        // Called by WebSocket events
        handleCampaignUpdated(campaignId: string) {
            this.refreshOneCampaign(campaignId, true)
        },

        async handleCampaignRefresh() {
            if (this.isRefreshing) return
            
            this.isRefreshing = true
            try {
                const { $customFetch } = useNuxtApp()
                
                // Force fresh since API has invalidated cache
                const response = await $customFetch<ApiResponseList<Campaign>>('/api/campaigns', {
                    method: 'GET',
                    params: {
                        include: 'router,sentCount,recipientsCount,deliveredCount,dispatchedCount',
                        hidePreview: 1,
                        ...this.tableParams,
                    },
                    headers: { 'X-Force-Fresh': '1' },
                })

                this.items = response.data
                this.meta = response.meta
                if (this.filters.period === 'errors') {
                    this.globalStats.error_count = response.meta?.total || 0
                }

                await this.fetchRecentCampaigns()
            } finally {
                this.isRefreshing = false
            }
        },

        async fetchGlobalCampaignStats(
            type: 'volume' | 'performance',
            params: { startDate: string; endDate: string; groupBy?: string }
        ) {
            this.statsLoading = true
            try {
                const { $customFetch } = useNuxtApp()
                const response = await $customFetch<{ data: { volume?: VolumeStatsItem[]; stats?: PerformanceStatsItem[] } }>('/api/campaigns/stats', {
                    params: {
                        type,
                        start_date: params.startDate,
                        end_date: params.endDate,
                        group_by: params.groupBy,
                    }
                })

                if (type === 'volume') {
                    this.volumeStats = response.data?.volume || []
                } else {
                    this.performanceStats = response.data?.stats || []
                }
                return response.data
            } catch (e) {
                console.error(`Failed to fetch ${type} stats:`, e)
                return null
            } finally {
                this.statsLoading = false
            }
        },
    },
})
